import React from 'react';
import { auth, loginWithGoogle, logOutFromGoogle, SubscribeUserAuthEvent } from '../services/firebase';

const AuthContext = React.createContext();

const isTokenExpiredError = (error) => {
    const code = error?.code || '';
    const message = (error?.message || '').toLowerCase();
    return code === 'auth/id-token-expired' || code === 'auth/user-token-expired' || (message.includes('token') && message.includes('expired'));
};

const isSecureTokenBlockedError = (error) => {
    const code = error?.code || '';
    const message = (error?.message || '').toLowerCase();
    return code.includes('requests-to-this-api') || message.includes('securetoken.granttoken-are-blocked') || message.includes('securetoken');
};

const AuthProvider = (props) => {
    const [user, setUser] = React.useState(null);

    SubscribeUserAuthEvent(setUser);
    
    const login = React.useCallback(async () => {
        const user = await loginWithGoogle();
        if (!user) {
            // TODO: Handle failed login
            return;
        }

        if (typeof user.getIdToken === 'function') {
            setUser(user);
        }
    }, []);

    const logout = React.useCallback(async () => {
        await logOutFromGoogle();
        setUser(null);
    }, []);

    const getIdToken = React.useCallback(async (forceRefresh = false) => {
        let currentUser = auth.currentUser || user;

        // On a fresh page load Firebase restores the persisted session
        // asynchronously; wait for it instead of treating the user as
        // signed out while initialization is still in flight.
        if (!currentUser && typeof auth.authStateReady === 'function') {
            await auth.authStateReady();
            currentUser = auth.currentUser;
        }

        if (!currentUser || typeof currentUser.getIdToken !== 'function') {
            throw new Error('Not authenticated. Please sign in again.');
        }

        try {
            return await currentUser.getIdToken(forceRefresh);
        } catch (error) {
            if (!forceRefresh && isTokenExpiredError(error)) {
                return await currentUser.getIdToken(true);
            }

            if (isSecureTokenBlockedError(error)) {
                try {
                    await logOutFromGoogle();
                } catch (_) {
                    // Ignore logout cleanup errors
                }
                setUser(null);
                throw new Error('Session refresh is currently blocked. Please sign in again.');
            }

            throw error;
        }
    }, [user]);

    const value = React.useMemo(() => ({ user, login, logout, getIdToken }), [user, login, logout, getIdToken]);

    return <AuthContext.Provider value={value} {...props} />;
};


export { AuthContext, AuthProvider };